import type { IAgentTool } from '../IAgentTool';

/**
 * RDM Validate Tool
 *
 * Validates RDM text and returns diagnostics with line numbers.
 * Useful for agents to check whether generated RDM is valid before applying.
 */
export const rdmValidateTool: IAgentTool = {
  name: 'rdm_validate',
  description:
    'Validate RDM text and return parse/validation diagnostics with line numbers. ' +
    'Use this to check whether RDM you are about to apply is syntactically and semantically correct.',
  parameters: {
    type: 'object',
    properties: {
      rdmText: {
        type: 'string',
        description: 'Full RDM document text (with frontmatter) or a fragment to validate.',
      },
    },
    required: ['rdmText'],
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params) {
    const rdmText = String(params.rdmText ?? '').trim();
    if (!rdmText) {
      return { success: false, summary: 'Missing rdmText parameter.' };
    }

    const { rdmService } = await import('../../../../domains/rdm/runtime');
    const result = rdmService.parseAndValidate(rdmText);

    if (result.success) {
      const nodeCount = countDecls(result.document!.structure.declarations, 'node');
      const edgeCount = countDecls(result.document!.structure.declarations, 'edge');
      return {
        success: true,
        summary: `Valid RDM: ${nodeCount} nodes, ${edgeCount} edges, ${result.warnings.length} warning(s).`,
        data: {
          valid: true,
          nodeCount,
          edgeCount,
          warnings: result.warnings,
        },
      };
    }

    return {
      success: true,
      summary: `Invalid RDM: ${result.errors.length} error(s).`,
      data: {
        valid: false,
        errors: result.errors.map((e) => ({
          code: e.code,
          message: e.message,
          line: e.line,
          column: e.column,
        })),
        warnings: result.warnings,
      },
    };
  },
};

function countDecls(declarations: Array<{ type: string; declarations?: any[] }>, type: string): number {
  let count = 0;
  for (const d of declarations) {
    if (d.type === type) count++;
    if (d.declarations) count += countDecls(d.declarations, type);
  }
  return count;
}
